// types
import type { RequestRollToPaymentsPayload } from '@makemydeal/dr-dash-types';
import type { GenericObject } from '@makemydeal/dr-platform-types';

export interface PSErrorDetails {
    lookupKey: string | null;
    actionForPayment: string;
    originalPSError: string;
    psErrorMessagePath: string;
}
export const getPSErrorPaymentDetails = (payload: any, lastPSFailureDetails: PSErrorDetails) => {
    const result: PSErrorDetails = {
        lookupKey: '',
        actionForPayment: '',
        originalPSError: '',
        psErrorMessagePath: ''
    };
    if (payload.error?.errors?.length) {
        const psErrorObj = payload.error.errors[0]?.originalPSError || payload.error.errors[0];
        let psErrorMessage: string | null = null;
        let psErrorField: string | null = null;
        const psValidationErrors =
            psErrorObj?.validationErrors || psErrorObj?.validation_errors || psErrorObj?.error?.validation_errors;
        if (psValidationErrors) {
            const errorDetails = getValidationErrorMessage(psValidationErrors);
            psErrorMessage = errorDetails.message;
            psErrorField = errorDetails.field;
        } else if (psErrorObj?.message) {
            psErrorMessage = psErrorObj.message;
        }

        if (psErrorMessage === lastPSFailureDetails?.originalPSError) {
            return lastPSFailureDetails;
        }
        const actionForPayment =
            payload.originalAction.meta?.paymentGrid?.mainPaymentActionType || payload.originalAction.type || '';
        result.lookupKey = getPSErrorLookupKey(actionForPayment, psErrorMessage || '');
        result.actionForPayment = actionForPayment;
        result.originalPSError = psErrorMessage || '';
        result.psErrorMessagePath = psErrorField || '';
    }
    return result;
};

export const getPSErrorLookupKey = (actionForPayment: string, psErrorMessage: string) => {
    if (psErrorMessage) {
        const lowercasePSErrorMsg: any = psErrorMessage.toLowerCase();
        const regex = /[a-z]/g;
        const onlyLowercaseCharacterError = lowercasePSErrorMsg.match(regex)?.join('') || '';
        const lowercaseActionForPayment = (actionForPayment.toLowerCase().match(regex) || []).join('');
        const paymentServiceMappingKey = lowercaseActionForPayment + '_' + onlyLowercaseCharacterError;
        return paymentServiceMappingKey;
    }
    return null;
};

export function getValidationErrorMessage(
    data: any,
    errorDetails = { field: '', message: '' },
    parentKey?: string // usefull when least key is number, we can use parent key as field name
): { field: string; message: string } {
    if (!data || errorDetails.message) {
        return errorDetails;
    }
    for (const [key, elm] of Object.entries(data)) {
        const fieldName = /^\d+$/.test(key) && parentKey ? parentKey : key; // if the key is a number and we have a parentKey, use the parentKey as the field name, otherwise use the key itself
        if (Array.isArray(elm)) {
            for (const value of elm) {
                if (value && typeof value === 'object') {
                    errorDetails = getValidationErrorMessage(value, errorDetails, key);
                } else {
                    errorDetails = { field: fieldName, message: value };
                    break;
                }
            }
        } else if (elm && typeof elm === 'object') {
            errorDetails = getValidationErrorMessage(elm, errorDetails, key);
        } else {
            errorDetails = { field: fieldName, message: elm as string };
        }
        if (errorDetails.message) {
            break;
        }
    }
    return errorDetails;
}

// Accoring to US1598052, we need to filter out messages with "***** FATAL" string
function shouldFilterMessage(message: GenericObject): boolean {
    return typeof message.message === 'string' && message.message.includes('***** FATAL');
}

export function parseErrorMessages(payload: RequestRollToPaymentsPayload): GenericObject[] | undefined {
    if (!payload) return undefined;

    const allMessages: GenericObject[] = [...(payload.messages || [])].filter((message) => !shouldFilterMessage(message));

    if (payload.rollProgramItems && Array.isArray(payload.rollProgramItems)) {
        payload.rollProgramItems.forEach((item: any) => {
            if (item.rollProgram && Array.isArray(item.rollProgram.messages)) {
                const filteredMessages = item.rollProgram.messages.filter(
                    (message: GenericObject) => !shouldFilterMessage(message)
                );
                allMessages.push(...filteredMessages);
            }
        });
    }

    return allMessages.length > 0 ? allMessages : undefined;
}
